/**
 * The checker feature module (W2-12a checker-runtime; DESIGN.md §5.10, §4.18, §8.2; WORKPACKAGES Appendix B:
 * `problems.*` incl. `problems.fix.*`).
 *
 *  - Dock tab "Problems" (panel id 'problems', slot chart.dock; the shell's problems chip opens it).
 *  - Commands: show problems, next / previous problem (Alt+] / Alt+[, Alt-free Ctrl+Shift+. / Ctrl+Shift+,; they
 *    seek, select and focus the line through `nav.reveal` and name the problem in a toast), quick fix (Ctrl+.),
 *    ignore the current problem, stop ignoring all, check now, and the 35 `problems.fix.*` commands (fixes.ts).
 *  - Timeline decoration provider: problem ticks in the gutter and the overview strip (decorations.ts).
 *  - Settings rows (Panels and tools): timeline marks off / without tips, explanations under every problem. The
 *    thresholds and the rule switches are rows of the Settings page itself (Settings > Checker, W2-13), fed by
 *    `problems.rules()`.
 *  - When-atom `problems.fixing` (holds while the problems service applies a fix; the fix commands themselves are
 *    runnable by any package and kept out of the palette's default list with `!paletteOpen`) and
 *    `init`: attaches the problems service to the app (registry, UI, skins, editor, audio) and switches off rules
 *    that start off.
 */

import type {
  CommandContext,
  CommandDef,
  FeatureModule,
  KeyBinding,
  SettingDef,
  ToastOptions,
} from '../../app/registry/types';
import type { Problem, Severity } from '../../core/check/types';
import type { MessageRef } from '../../core/util/types';
import { m } from './checker.messages';
import { createProblemDecorations, HIDE_TICKS_KEY, HIDE_TIP_TICKS_KEY } from './decorations';
import { FIXING_ATOM, fixCommandDefs } from './fixes';
import { ALL_CATEGORIES, revealProblem } from './navigation';
import { EXPLAIN_ALL_KEY, ProblemsPanel } from './ProblemsPanel';
import { asCheckerService } from './problemsService';

export const PROBLEMS_PANEL_ID = 'problems';

function ref(key: string, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

function noProject(ctx: CommandContext): MessageRef | null {
  return ctx.store === null ? ref(m.reasonNoProject) : null;
}

function toastKind(p: Problem): NonNullable<ToastOptions['kind']> {
  return p.severity === 'fatal' || p.severity === 'error' ? 'error' : p.severity === 'warning' ? 'warning' : 'info';
}

/** Next / previous problem: seek, select and focus its line; name it in a toast with its first fix. */
async function step(ctx: CommandContext, dir: 1 | -1): Promise<void> {
  const p = ctx.problems.next(dir);
  if (p === null) {
    ctx.ui.toast(ref(m.toastNoProblems), { kind: 'info' });
    return;
  }
  const checker = asCheckerService(ctx.problems);
  if (checker !== null) await checker.reveal(p.id);
  else await revealProblem(ctx, p);
  const first = p.fixes[0];
  ctx.ui.toast(
    p.message,
    first === undefined
      ? { kind: toastKind(p) }
      : { kind: toastKind(p), action: { label: first.label, command: 'problems.quickFix' } },
  );
}

const SEVERITY_SET: ReadonlySet<string> = new Set<Severity>(['fatal', 'error', 'warning', 'tip']);

/** The `severities` of a `problems.show` argument, or null when absent or invalid. */
function severitiesArg(args: unknown): readonly Severity[] | null {
  if (typeof args !== 'object' || args === null) return null;
  const v = (args as { readonly severities?: unknown }).severities;
  if (!Array.isArray(v) || !v.every((x) => typeof x === 'string' && SEVERITY_SET.has(x))) return null;
  return v as Severity[];
}

const commands: CommandDef[] = [
  {
    id: 'problems.show',
    title: m.cmdShow,
    category: 'View',
    icon: 'TriangleAlert',
    level: 'guided',
    aliases: ['checker', 'auto correct', 'A.C.', 'errors', 'warnings', '检查', '自动纠错', '问题'],
    menus: [{ menu: 'app.view', group: 'problems', order: 10 }],
    run: (ctx, args) => {
      // `severities` (CommandArgs, CR-W2-09-5): show exactly these severities (every category, the whole chart).
      const severities = severitiesArg(args);
      const checker = asCheckerService(ctx.problems);
      if (severities !== null && checker !== null) {
        checker.setFilter({ severities, categories: ALL_CATEGORIES, scope: 'chart', query: '' });
      }
      ctx.ui.showPanel(PROBLEMS_PANEL_ID);
    },
  },
  {
    id: 'problems.next',
    title: m.cmdNext,
    category: 'View',
    icon: 'ChevronsDown',
    level: 'guided',
    hint: 20,
    repeat: true,
    aliases: ['go to problem', 'jump', '跳转'],
    menus: [{ menu: 'app.view', group: 'problems', order: 20 }],
    enabled: (ctx) => noProject(ctx) ?? (ctx.problems.problems().length > 0 ? true : ref(m.reasonNoProblems)),
    run: (ctx) => step(ctx, 1),
  },
  {
    id: 'problems.prev',
    title: m.cmdPrev,
    category: 'View',
    icon: 'ChevronsUp',
    level: 'guided',
    repeat: true,
    menus: [{ menu: 'app.view', group: 'problems', order: 30 }],
    enabled: (ctx) => noProject(ctx) ?? (ctx.problems.problems().length > 0 ? true : ref(m.reasonNoProblems)),
    run: (ctx) => step(ctx, -1),
  },
  {
    id: 'problems.quickFix',
    title: m.cmdQuickFix,
    category: 'Edit',
    icon: 'Wrench',
    level: 'guided',
    hint: 25,
    aliases: ['fix', 'repair', '修复'],
    menus: [{ menu: 'app.edit', group: 'problems', order: 10 }],
    enabled: (ctx) => {
      const no = noProject(ctx);
      if (no !== null) return no;
      const checker = asCheckerService(ctx.problems);
      if (checker === null) return ref(m.reasonNoCurrent);
      return checker.quickFixTarget() === null ? ref(m.reasonNoCurrent) : true;
    },
    run: async (ctx) => {
      const target = asCheckerService(ctx.problems)?.quickFixTarget() ?? null;
      if (target === null) {
        ctx.ui.toast(ref(m.toastNoTarget), { kind: 'info' });
        return;
      }
      if (target.fixes.length === 0) {
        // Nothing to repair: go to it (what its "Go to" fix did before fixes that only go there were left out).
        const checker = asCheckerService(ctx.problems);
        const went = checker === null ? false : await checker.reveal(target.id);
        if (!went) ctx.ui.toast(ref(m.toastNoFix), { kind: 'info' });
        return;
      }
      await ctx.problems.applyFix(target.id, 0);
    },
  },
  {
    id: 'problems.ignore',
    title: m.cmdIgnore,
    category: 'Edit',
    icon: 'EyeOff',
    level: 'guided',
    menus: [{ menu: 'app.edit', group: 'problems', order: 20 }],
    enabled: (ctx) => {
      const no = noProject(ctx);
      if (no !== null) return no;
      const checker = asCheckerService(ctx.problems);
      const cur = checker?.current() ?? null;
      return cur !== null && checker?.get(cur) !== undefined ? true : ref(m.reasonNoCurrent);
    },
    run: (ctx) => {
      const checker = asCheckerService(ctx.problems);
      const cur = checker?.current() ?? null;
      if (checker === null || cur === null) {
        ctx.ui.toast(ref(m.toastNoTarget), { kind: 'info' });
        return;
      }
      checker.ignore(cur);
    },
  },
  {
    id: 'problems.unignoreAll',
    title: m.cmdUnignoreAll,
    category: 'Edit',
    icon: 'Eye',
    level: 'full',
    enabled: (ctx) => {
      const no = noProject(ctx);
      if (no !== null) return no;
      return (ctx.store?.doc.editor.ignoredProblems.length ?? 0) > 0 ? true : ref(m.reasonNoIgnored);
    },
    run: (ctx) => {
      const keys = ctx.store?.doc.editor.ignoredProblems ?? [];
      const checker = asCheckerService(ctx.problems);
      if (checker !== null && checker.unignore(keys)) {
        ctx.ui.toast(ref(m.toastUnignored, { count: keys.length }), { kind: 'info', undo: true });
      }
    },
  },
  {
    id: 'problems.checkNow',
    title: m.cmdCheckNow,
    category: 'Tools',
    icon: 'RefreshCw',
    level: 'guided',
    menus: [{ menu: 'tools.menu', group: 'check', order: 10 }],
    enabled: (ctx) => noProject(ctx) ?? true,
    run: (ctx) => {
      ctx.problems.fullCheck();
    },
  },
  ...fixCommandDefs({ pendingLabel: (ctx) => asCheckerService(ctx.problems)?.pendingFixLabel() ?? null }),
];

const bindings: readonly KeyBinding[] = [
  { chord: 'Alt+BracketRight', command: 'problems.next', altFree: 'Ctrl+Shift+Period' },
  { chord: 'Alt+BracketLeft', command: 'problems.prev', altFree: 'Ctrl+Shift+Comma' },
  { chord: 'Ctrl+Period', command: 'problems.quickFix' },
];

const settings: readonly SettingDef[] = [
  {
    path: `ext.${HIDE_TICKS_KEY}`,
    title: m.setHideTicks,
    description: m.setHideTicksDesc,
    level: 'guided',
    kind: 'boolean',
  },
  {
    path: `ext.${HIDE_TIP_TICKS_KEY}`,
    title: m.setHideTipTicks,
    description: m.setHideTipTicksDesc,
    level: 'guided',
    kind: 'boolean',
  },
  {
    path: `ext.${EXPLAIN_ALL_KEY}`,
    title: m.setExplainAll,
    description: m.setExplainAllDesc,
    level: 'guided',
    kind: 'boolean',
  },
];

export default {
  id: 'checker',
  commands,
  keybindings: { default: bindings, rpe: bindings },
  panels: [
    {
      id: PROBLEMS_PANEL_ID,
      title: m.panelTitle,
      icon: 'TriangleAlert',
      slot: 'chart.dock',
      order: 20,
      level: 'guided',
      component: ProblemsPanel,
    },
  ],
  settings,
  timelineDecorations: (s) => createProblemDecorations(s),
  whenAtoms: (s) => ({
    [FIXING_ATOM]: () => asCheckerService(s.problems)?.fixing() === true,
  }),
  // Problems are explained by the Help panel's "Problems" article (onboarding/helpTopics.ts): one article per subject.
  helpTopics: [],
  init(s) {
    const detach = asCheckerService(s.problems)?.attach(s);
    return () => {
      detach?.();
    };
  },
} satisfies FeatureModule;
